import React, { FunctionComponent, useState, useEffect, } from "react";
import { Link } from "react-router-dom";
import { Button, Icon } from "semantic-ui-react";

import { getSidebarMenuItems, getBottomMenuItems } from "../../constants/sidebarMenuItems";
import FlexibleImage from "../common/FlexibleImage";
import NavigationMenu from "./NavigationMenu";
import { FavoritesSection } from "./FavoritesSection";
import SidebarUserProfile from "./SidebarUserProfile";
import type { Session } from "../../modules/session";

import logoFull from "../../images/GR_LOGO_Primary.png";
import logoIcon from "../../images/GR_Icon_Primary.png";
import "./Sidebar.css";

interface SidebarProps {
  location: any;
  session: Session;
  organization: any;
  currentUserId: string;
  firstName: string;
  onLogOut: () => void;
  onDeleteImpersonation: () => void;
}

const Sidebar: FunctionComponent<SidebarProps> = ({
  location,
  session,
  organization,
  currentUserId,
  firstName,
  onLogOut,
  onDeleteImpersonation,
}) => {
  const [isOpen, setIsOpen] = useState(() => {
    const saved = sessionStorage.getItem("sidebar-open");
    return saved ? JSON.parse(saved) : false;
  });

  // Track whether this is the initial render to prevent unwanted animations
  const [isFirstRender, setIsFirstRender] = useState(true);

  const menuItems = getSidebarMenuItems(session, organization);
  const bottomMenuItems = getBottomMenuItems(session, currentUserId);

  // Reset sidebar state when roles change (e.g., during impersonation)
  useEffect(() => {
    // Only reset if the user ID actually changes (real impersonation)
    // Don't reset on initial mount or page refresh
    const savedUserId = sessionStorage.getItem("sidebar-user-id");
    if (savedUserId && parseInt(savedUserId) !== parseInt(currentUserId)) {
      setIsOpen(false);
      sessionStorage.removeItem("sidebar-open");
    }
    // Save current user ID for comparison
    sessionStorage.setItem("sidebar-user-id", currentUserId);
  }, [currentUserId]);

  useEffect(() => {
    // On initial mount, notify parent of current state and save to sessionStorage
    sessionStorage.setItem("sidebar-open", JSON.stringify(isOpen));

    // Allow transitions after initial render
    const timer = setTimeout(() => {
      setIsFirstRender(false);
    }, 50); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // Save to sessionStorage when state changes (but don't notify parent to avoid loops)
  useEffect(() => {
    sessionStorage.setItem("sidebar-open", JSON.stringify(isOpen));
  }, [isOpen]);


  const getLocationBaseRoute = () => {
    const pathname = location.pathname;

    // Handle home route
    if (pathname === "/" || pathname === "/home") {
      return "home";
    }

    const pathList = pathname.split("/");

    // Handle sub-routes for campaigns
    if (pathname.includes("/campaigns/")) {
      return "campaigns";
    }

    // Handle accounts routes
    if (pathname.includes("/accounts/")) {
      return "accounts";
    }

    // Handle other specific routes
    if (pathname.includes("/fundraisers")) {
      return "fundraisers";
    }

    if (pathname.includes("/recurring")) {
      return "recurring";
    }

    if (pathname.includes("/donations")) {
      return "donations";
    }

    if (pathname.includes("/sales")) {
      return "sales";
    }

    if (pathname.includes("/vip-program")) {
      return "vip-program";
    }

    if (pathname.includes("/events")) {
      return "events";
    }

    if (pathname.includes("/organizations")) {
      return "organizations";
    }

    if (pathname.includes("/tap-n-donate")) {
      return "tap-n-donate";
    }

    return pathList.length >= 2 ? pathList[1] : null;
  };
  const getActiveSubRoute = () => {
    const pathname = location.pathname;

    // Campaigns sub-routes
    if (pathname.includes("/campaigns/live")) return "campaigns-live";
    if (pathname.includes("/campaigns/complete")) return "campaigns-complete";
    if (pathname.includes("/campaigns/archived")) return "campaigns-archived";

    // Fundraisers sub-routes
    if (pathname.includes("/fundraisers/all")) return "fundraisers-all";
    if (pathname.includes("/fundraisers/active")) return "fundraisers-active";
    if (pathname.includes("/fundraisers/inactive")) return "fundraisers-inactive";

    // Recurring sub-routes
    if (pathname.includes("/recurring/all")) return "recurring-all";
    if (pathname.includes("/recurring/active")) return "recurring-active";
    if (pathname.includes("/recurring/paused")) return "recurring-paused";

    // Sales sub-routes
    if (pathname.includes("/sales/tickets")) return "sales-tickets";
    if (pathname.includes("/sales/merchandise")) return "sales-merchandise";
    if (pathname.includes("/sales") && !pathname.includes("/sales/")) return "sales-all";

    // Accounts sub-routes
    if (pathname.includes("/accounts/all")) return "accounts-all";
    if (pathname.includes("/accounts/admins")) return "accounts-admins";
    if (pathname.includes("/accounts/fundraisers")) return "accounts-fundraisers";
    if (pathname.includes("/accounts/donors")) return "accounts-donors";

    return null;
  };

  const baseRoute = getLocationBaseRoute();
  const activeSubRoute = getActiveSubRoute();

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div className={`sidebar${isOpen ? " open" : ""}${isFirstRender ? " no-transition" : ""}`}>
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo-link">
          {isOpen ? (
            <FlexibleImage
              size="small"
              shape="square"
              imageUrl={logoFull}
              className="sidebar-logo"
              alt="Grassrootz Logo"
              showPlaceholder={false}
            />
          ) : (
            <FlexibleImage
              shape="square"
              imageUrl={logoIcon}
              className="sidebar-logo-icon"
              alt="Grassrootz Icon"
              showPlaceholder={false}
            />
          )}
        </Link>
        <Button
          className="sidebar-toggle"
          onClick={toggleOpen}
          icon={isOpen ? "angle left" : "angle right"}
        />
      </div>
      <div className="sidebar-content">
        <NavigationMenu
          items={menuItems}
          currentRoute={baseRoute}
          activeSubRoute={activeSubRoute}
          session={session}
          isMinimized={!isOpen}
          currentUserId={currentUserId}
          currentPath={location.pathname}
        />
        {session.isSystemAdmin && (
          <FavoritesSection
            currentPath={location.pathname + location.search}
            isMinimized={!isOpen}
          />
        )}
      </div>

      <div className="sidebar-footer">
        {bottomMenuItems.length > 0 && (
          <NavigationMenu
            items={bottomMenuItems}
            currentRoute={baseRoute}
            activeSubRoute={activeSubRoute}
            session={session}
            isBottomMenu={true}
            isMinimized={!isOpen}
            currentUserId={currentUserId}
            currentPath={location.pathname}
          />
        )}
        <SidebarUserProfile
          firstName={firstName}
          currentUserId={currentUserId}
          impersonated={session.impersonated}
          session={session}
          onLogOut={onLogOut}
          onDeleteImpersonation={onDeleteImpersonation}
          isMinimized={!isOpen}
        />
      </div>
    </div>
  );
};

export default Sidebar;
